import { useEffect, useImperativeHandle, forwardRef, useState } from "react";
import { createRoot } from "react-dom/client";
import api from "@/lib/api";

/**
 * MentionSuggestionList (iter157) — Popup-Liste für Inline-Mentions.
 * Von Tiptap-Suggestion via Ref angesteuert. Debounced fetch von /api/mentions/search.
 */
const MentionSuggestionList = forwardRef(function MentionSuggestionList(props, ref) {
  const { items, command } = props;
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => setSelectedIndex(0), [items]);

  const selectItem = (index) => {
    const it = items[index];
    if (!it) return;
    command({
      id: it.handle,
      label: it.display_name || it.handle,
      "data-mention": it.handle,
    });
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === "ArrowDown") {
        setSelectedIndex((i) => (i + 1) % Math.max(items.length, 1));
        return true;
      }
      if (event.key === "ArrowUp") {
        setSelectedIndex((i) => (i - 1 + items.length) % Math.max(items.length, 1));
        return true;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        selectItem(selectedIndex);
        return true;
      }
      return false;
    },
  }));

  if (!items.length) {
    return (
      <div className="bg-white border border-[#0A0A0A] shadow-xl rounded-lg px-3 py-2 text-xs text-[#999]">
        Keine Treffer
      </div>
    );
  }

  return (
    <div
      data-testid="mention-suggestion-list"
      className="bg-white border border-[#0A0A0A] shadow-xl rounded-lg overflow-hidden max-h-52 overflow-y-auto min-w-[220px]"
    >
      {items.map((it, i) => (
        <button
          type="button"
          key={it.handle}
          onClick={() => selectItem(i)}
          data-testid={`mention-suggestion-item-${it.handle}`}
          className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${
            i === selectedIndex ? "bg-[#F5F5F5]" : "hover:bg-[#F5F5F5]"
          }`}
        >
          {it.avatar_url ? (
            <img src={it.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" />
          ) : (
            <div className="w-6 h-6 rounded-full bg-[#E5E5E5] flex items-center justify-center text-[10px] font-bold text-[#666] shrink-0">
              {(it.display_name || it.handle || "?")[0].toUpperCase()}
            </div>
          )}
          <span className="text-sm text-[#0A0A0A] truncate">{it.display_name || it.handle}</span>
        </button>
      ))}
    </div>
  );
});

export default MentionSuggestionList;

/**
 * Suggestion-Konfiguration für Tiptap-Mention-Extension.
 * Rendert die Popup-List an der Caret-Position und ruft /api/mentions/search.
 */
export function createMentionSuggestion() {
  return {
    char: "@",
    allowedPrefixes: [null, " ", "\n"],
    items: async ({ query }) => {
      const q = (query || "").trim();
      if (q.length < 1) return [];
      try {
        const { data } = await api.get(`/mentions/search?q=${encodeURIComponent(q)}&limit=6`);
        return data?.results || [];
      } catch {
        return [];
      }
    },
    render: () => {
      let container = null;
      let root = null;
      let componentRef = { current: null };

      const mount = (props) => {
        container = document.createElement("div");
        container.style.position = "absolute";
        container.style.zIndex = "9999";
        document.body.appendChild(container);
        root = createRoot(container);
        renderList(props);
      };

      const renderList = (props) => {
        if (!root) return;
        const setRef = (r) => (componentRef.current = r);
        root.render(<MentionSuggestionList ref={setRef} {...props} />);
      };

      const updatePosition = (rect) => {
        if (!container || !rect) return;
        container.style.top = `${rect.bottom + window.scrollY + 4}px`;
        container.style.left = `${rect.left + window.scrollX}px`;
      };

      return {
        onStart: (props) => {
          mount(props);
          updatePosition(props.clientRect && props.clientRect());
        },
        onUpdate: (props) => {
          renderList(props);
          updatePosition(props.clientRect && props.clientRect());
        },
        onKeyDown: (props) => {
          if (props.event.key === "Escape") {
            props.event.preventDefault();
            return true;
          }
          return componentRef.current?.onKeyDown ? componentRef.current.onKeyDown(props) : false;
        },
        onExit: () => {
          if (root) root.unmount();
          if (container && container.parentNode) container.parentNode.removeChild(container);
          container = null;
          root = null;
          componentRef = { current: null };
        },
      };
    },
  };
}
